// The schedule editor's presets, and the cron each one writes. Anything a
// preset can't write exactly is Custom: the raw expression, untouched.

export type ScheduleKind =
  "custom" | "daily" | "hourly" | "weekdays" | "weekly";

/** One flat draft, so switching presets keeps the time already chosen. */
export type ScheduleDraft = {
  readonly custom: string;
  readonly hour: number;
  readonly kind: ScheduleKind;
  readonly minute: number;
  /** 0 is Sunday, as in cron. */
  readonly weekday: number;
};

export const DEFAULT_SCHEDULE: ScheduleDraft = {
  custom: "",
  hour: 9,
  kind: "daily",
  minute: 0,
  weekday: 1,
};

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const toCron = (draft: ScheduleDraft): string => {
  const { hour, minute } = draft;
  switch (draft.kind) {
    case "custom":
      return draft.custom.trim();
    case "daily":
      return `${minute} ${hour} * * *`;
    case "hourly":
      return `${minute} * * * *`;
    case "weekdays":
      return `${minute} ${hour} * * 1-5`;
    case "weekly":
      return `${minute} ${hour} * * ${draft.weekday}`;
  }
};

const PRESET_SHAPE = /^(\d+) (\d+|\*) \* \* (\*|1-5|\d)$/;

export const fromCron = (cron: string): ScheduleDraft => {
  const custom: ScheduleDraft = {
    ...DEFAULT_SCHEDULE,
    custom: cron,
    kind: "custom",
  };
  const match = PRESET_SHAPE.exec(cron);
  if (!match) {
    return custom;
  }
  const [, minute = "", hour = "", weekday = ""] = match;
  const kind: ScheduleKind =
    hour === "*"
      ? "hourly"
      : weekday === "*"
        ? "daily"
        : weekday === "1-5"
          ? "weekdays"
          : "weekly";
  const draft: ScheduleDraft = {
    custom: cron,
    hour: hour === "*" ? DEFAULT_SCHEDULE.hour : Number(hour),
    kind,
    minute: Number(minute),
    weekday: kind === "weekly" ? Number(weekday) : DEFAULT_SCHEDULE.weekday,
  };
  // Writing it back must give the same text: that rules out `09` for `9`,
  // `7` for Sunday, an hourly that names a weekday and anything out of range.
  const inRange = draft.minute <= 59 && draft.hour <= 23 && draft.weekday <= 6;
  return inRange && toCron(draft) === cron ? draft : custom;
};
